import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaSearch, FaUndo, FaTrash, FaCheckCircle, FaExclamationCircle, FaHourglassHalf } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import Pagination from "Components/Pagination";
import { getResizeFileDownload, getSelfList, selfMultiDelete } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";
import { catchError, changeState, commonModalSetting } from "JS/common";
import useQueryParams from "utils/useQueryParams";
import useSyncPageWithQuery from 'utils/useSyncPageWithQuery';
import $ from "jquery";
import { listPageStyles } from "utils/designSystem";

const SelfApplication = () => {
  const { getQueryParamsObject, updateQueryParams, resetQueryParams } = useQueryParams();
  const [resetTrigger, setResetTrigger] = useState(false);
  const [selfData, setSelfData] = useState();
  const [pageInfo, setPageInfo] = useState(() => {
    const queryParams = getQueryParamsObject();
    return {
      page: Number(queryParams.page) || 1,
      limit: 10,
      totalPage: 1,
    };
  });
  useSyncPageWithQuery(setPageInfo)
  const [searchData, setSearchData] = useState(() => {
    const queryParams = getQueryParamsObject();
    return {
      markName: queryParams.markName || "",
      templateName: queryParams.templateName || "",
      applicantName: queryParams.applicantName || "",
      startDate: queryParams.startDate || "",
      endDate: queryParams.endDate || "",
      markType: queryParams.markType || "all",
    };
  });
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  let prevent = false;
  const today = new Date();
  const navigate = useNavigate();
  useMarkMenuSeen("self_application");
  // 체크된 항목 ID들을 저장하는 상태
  const [idArr, setIdArr] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  // 스타일 정의
  const styles = {
    container: listPageStyles.container,
    header: listPageStyles.header,
    title: listPageStyles.title,
    searchCard: listPageStyles.searchCard,
    searchRow: {
      display: 'flex',
      gap: '20px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    searchField: {
      flex: 1,
      minWidth: '200px'
    },
    searchLabel: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '6px'
    },
    searchInput: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#1e293b'
    },
    searchSelect: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      color: '#1e293b',
      cursor: 'pointer'
    },
    dateGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    searchButton: listPageStyles.searchButton,
    resetButton: listPageStyles.resetButton,
    deleteSection: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '16px'
    },
    deleteButton: listPageStyles.deleteButton,
    tableContainer: listPageStyles.tableContainer,
    tableWrapper: listPageStyles.tableWrapper,
    table: listPageStyles.table,
    th: listPageStyles.th,
    td: listPageStyles.td,
    checkbox: listPageStyles.checkbox,
    checkboxLabel: listPageStyles.checkboxLabel,
    markImage: {
      width: '40px',
      height: '40px',
      objectFit: 'contain',
      borderRadius: '4px',
      border: '1px solid #e2e8f0'
    },
    markType: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      textAlign: 'center'
    },
    typeText: {
      backgroundColor: '#dbeafe',
      color: '#1d4ed8'
    },
    typeFigure: {
      backgroundColor: '#dcfce7',
      color: '#16a34a'
    },
    typeCompound: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    userInfo: listPageStyles.userInfo,
    userName: {
      fontWeight: '500',
      marginBottom: '2px'
    },
    userId: {
      fontSize: '12px',
      color: '#6b7280'
    },
    reviewStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '600'
    },
    statusRequest: {
      color: '#dc2626'
    },
    statusCompleted: {
      color: '#059669'
    },
    statusPending: {
      color: '#6b7280'
    }
  };

  // 상표 유형별 스타일 반환
  const getMarkTypeStyle = (type) => {
    const baseStyle = styles.markType;
    switch (type) {
      case 'text':
        return { ...baseStyle, ...styles.typeText };
      case 'figure':
        return { ...baseStyle, ...styles.typeFigure };
      case 'compound':
        return { ...baseStyle, ...styles.typeCompound };
      default:
        return { ...baseStyle, ...styles.typeText };
    }
  };

  // 상표 유형 텍스트 반환
  const getMarkTypeText = (type) => {
    switch (type) {
      case 'text':
        return '문자상표';
      case 'figure':
        return '도형상표';
      case 'compound':
        return '복합상표';
      default:
        return '문자상표';
    }
  };

  // 검토 상태 스타일 반환
  const getReviewStatusStyle = (status) => {
    const baseStyle = styles.reviewStatus;
    switch (status) {
      case 'request':
        return { ...baseStyle, ...styles.statusRequest };
      case 'completed':
        return { ...baseStyle, ...styles.statusCompleted };
      default:
        return { ...baseStyle, ...styles.statusPending };
    }
  };

  // 검토 상태 아이콘 및 텍스트 반환
  const getReviewStatusContent = (status) => {
    switch (status) {
      case 'request':
        return { icon: <FaHourglassHalf size={12} />, text: '검토 요청' };
      case 'completed':
        return { icon: <FaCheckCircle size={12} />, text: '검토 완료' };
      default:
        return { icon: <FaExclamationCircle size={12} />, text: '미요청' };
    }
  };

  const getList = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => (prevent = false), 200);

    setSelfData([]);

    const result = await getSelfList(pageInfo, searchData);

    if (typeof result === "object") {
      const data = result?.data?.data;
      setSelfData(data);

      const imageFetches = data.map(async (item) => {
        let fileName = "";
        let originalName = "";

        if (item.mark_type === "figure" || item.mark_type === "text") {
          fileName = item.image_filename;
          originalName = item.image_original_filename;
        } else if (item.mark_type === "compound") {
          fileName = item.compound_image_filename;
          originalName = "복합상표";
        }

        if (fileName && originalName) {
          const url = await fetchUploadedImage(fileName, originalName);
          return { id: item.id, url };
        }

        return { id: item.id, url: null };
      });

      const resolvedImages = await Promise.all(imageFetches);

      const imageMap = {};
      resolvedImages.forEach(({ id, url }) => {
        if (url) imageMap[id] = url;
      });

      setImageUrls(imageMap);

      setPageInfo((prev) => ({
        ...prev,
        totalPage: result?.data?.meta?.total_page,
      }));
    } else {
      catchError(result, navigate, setAlertBox, setAlert);
    }
  };

  useEffect(() => {
    getList();
  }, [pageInfo?.page, resetTrigger]);

  const fetchUploadedImage = async (fileName, originalFileName) => {
    try {
      const response = await getResizeFileDownload(fileName, originalFileName);
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      const mimeType = response.headers["content-type"];
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error("이미지 불러오기 실패:", error);
      return null;
    }
  };

  // 체크박스 개별 선택
  const checkEach = (e) => {
    e.stopPropagation(); // 체크박스에서 모달 금지
    let all = $(".selfapplication-check").length;
    let checked = $(".selfapplication-check:checked").length;
    let arr = [];
    if (all !== checked) $(".selfapplication-all-check").prop("checked", false);
    else $(".selfapplication-all-check").prop("checked", true);
    for (let i = 0; i < all; i++) {
      if (document.getElementsByClassName("selfapplication-check")[i].checked) {
        const val = document.getElementsByClassName("selfapplication-check")[i]
          .value;
        arr.push(val);
        setIdArr(arr);
      }
    }
  };

  //= 다중 삭제
  const deleteSelf = async () => {
    const data = idArr;
    const result = await selfMultiDelete(data);
    if (typeof result === "object") {
      commonModalSetting(setAlertBox, true, "alert", "삭제되었습니다.");
      $(".selfapplication-all-check").prop("checked", false);
      $(".selfapplication-check").prop("checked", false);
      changeState(setPageInfo, "page", 1);
      setIdArr([]);
      setAlert("completeDelete");
      commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "정상적으로 삭제되었습니다."
      );
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  // 전체 선택
  const checkAll = () => {
    let arr = [];
    if ($(".selfapplication-all-check").is(":checked")) {
      $(".selfapplication-check").prop("checked", true);
      const all = $(".selfapplication-check").length;
      for (let i = 0; i < all; i++) {
        const val = document.getElementsByClassName("selfapplication-check")[i]
          .value;
        arr.push(val);
        setIdArr(arr);
      }
    } else {
      $(".selfapplication-check").prop("checked", false);
      setIdArr([]);
    }
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 셀프 출원 관리";
  }, []);

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaFileAlt />
          SELF-APPLICATION
        </h1>
      </div>

      {/* 검색 영역 */}
      <div style={styles.searchCard}>
        {/* 첫 번째 행 */}
        <div style={styles.searchRow}>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>출원상표명</label>
            <input
              type="text"
              placeholder="출원 상표명을 입력해 주세요."
              value={searchData?.markName}
              onChange={(e) =>
                changeState(setSearchData, "markName", e.target.value)
              }
              style={styles.searchInput}
            />
          </div>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>템플릿명</label>
            <input
              type="text"
              placeholder="템플릿명을 입력해 주세요."
              value={searchData?.templateName}
              onChange={(e) =>
                changeState(setSearchData, "templateName", e.target.value)
              }
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* 두 번째 행 */}
        <div style={styles.searchRow}>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>출원인</label>
            <input
              type="text"
              placeholder="출원인을 입력해 주세요."
              value={searchData?.applicantName}
              onChange={(e) =>
                changeState(setSearchData, "applicantName", e.target.value)
              }
              style={styles.searchInput}
            />
          </div>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>신청일</label>
            <div style={styles.dateGroup}>
              <input
                type="date"
                max={`${today.getFullYear()}-${today.getMonth() + 1 < 10
                  ? `0${today.getMonth() + 1}`
                  : today.getMonth() + 1
                  }-${today.getDate() + 1 < 10
                    ? `0${today.getDate()}`
                    : today.getDate()
                  }`}
                value={searchData.startDate}
                onChange={(e) =>
                  changeState(setSearchData, "startDate", e.target.value)
                }
                style={styles.searchInput}
              />
              <span style={{ color: '#6b7280' }}>~</span>
              <input
                type="date"
                max={`${today.getFullYear()}-${today.getMonth() + 1 < 10
                  ? `0${today.getMonth() + 1}`
                  : today.getMonth() + 1
                  }-${today.getDate() + 1 < 10
                    ? `0${today.getDate()}`
                    : today.getDate()
                  }`}
                value={searchData.endDate}
                onChange={(e) =>
                  changeState(setSearchData, "endDate", e.target.value)
                }
                style={styles.searchInput}
              />
            </div>
          </div>
        </div>

        {/* 세 번째 행 */}
        <div style={{...styles.searchRow, marginBottom: 0}}>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>상표유형</label>
            <select
              value={searchData.markType}
              onChange={(e) =>
                changeState(setSearchData, "markType", e.target.value)
              }
              style={styles.searchSelect}
            >
              <option value="all">선택</option>
              <option value="text">문자상표</option>
              <option value="figure">도형상표</option>
              <option value="compound">복합상표</option>
            </select>
          </div>
          <div style={{...styles.searchField, display: 'flex', alignItems: 'end'}}>
            <div style={styles.buttonGroup}>
              <button
                style={styles.searchButton}
                onClick={() => {
                  setPageInfo((prev) => ({
                    ...prev,
                    page: 1,
                  }));
                  getList();
                  updateQueryParams({ ...searchData, page: 1 });
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <FaSearch />
                검색
              </button>
              <button
                style={styles.resetButton}
                onClick={() => {
                  setSearchData({
                    markName: "",
                    templateName: "",
                    applicantName: "",
                    startDate: "",
                    endDate: "",
                    markType: "all",
                  });
                  setPageInfo((prev) => ({
                    ...prev,
                    page: 1,
                  }));
                  resetQueryParams();
                  setResetTrigger((prev) => !prev)
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
              >
                <FaUndo />
                초기화
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 삭제 버튼 */}
      <div style={styles.deleteSection}>
        <button
          style={styles.deleteButton}
          onClick={() => {
            if (!idArr.length)
              return commonModalSetting(
                setAlertBox,
                true,
                "alert",
                "삭제할 글을 선택해 주세요."
              );
            else {
              setAlert("deleteConfirm");
              commonModalSetting(
                setAlertBox,
                true,
                "confirm",
                "삭제하면 다시 복구할 수 없습니다.<br/>정말 삭제하시겠습니까?"
              );
            }
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
        >
          <FaTrash />
          삭제하기
        </button>
      </div>

      {/* 테이블 */}
      <div style={styles.tableContainer}>
        <div style={styles.tableWrapper} className="table-wrapper">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className="selfapplication-all-check"
                      onChange={checkAll}
                      style={styles.checkbox}
                    />
                  </label>
                </th>
                <th style={styles.th}>No.</th>
                <th style={styles.th}>템플릿명</th>
                <th style={styles.th}>출원상표명</th>
                <th style={styles.th}>상표유형</th>
                <th style={styles.th}>출원인</th>
                <th style={styles.th}>사용자정보</th>
                <th style={styles.th}>등록일</th>
                <th style={styles.th}>검토 여부</th>
              </tr>
            </thead>
            <tbody>
              {selfData?.length > 0 ? (
                selfData?.map(
                  (
                    {
                      id,
                      template_name,
                      mark_name,
                      mark_type,
                      applicant_name,
                      user_name,
                      created_at,
                      user_id,
                      review_status
                    },
                    idx
                  ) => {
                    const checkDragText = (e) => {
                      const selection = window.getSelection();
                      if (selection.toString().length > 0) {
                        return;
                      }
                      navigate(`/self-application/${id}`);
                    };

                    const statusContent = getReviewStatusContent(review_status);

                    return (
                      <tr 
                        key={id} 
                        onClick={() => checkDragText()}
                        onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                      >
                        <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                          <label style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              className="selfapplication-check"
                              onChange={checkEach}
                              value={id}
                              style={styles.checkbox}
                            />
                          </label>
                        </td>
                        <td style={styles.td}>{id}</td>
                        <td style={styles.td}>{template_name}</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {imageUrls?.[id] && (
                              <img
                                src={imageUrls[id]}
                                alt="썸네일"
                                style={styles.markImage}
                              />
                            )}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={getMarkTypeStyle(mark_type)}>
                            {getMarkTypeText(mark_type)}
                          </span>
                        </td>
                        <td style={styles.td}>{applicant_name?.split(",")[0]}</td>
                        <td style={styles.td}>
                          <div style={styles.userInfo}>
                            <div style={styles.userName}>{user_name}</div>
                            <div style={styles.userId}>({user_id})</div>
                          </div>
                        </td>
                        <td style={styles.td}>{created_at?.replace("T", " ")}</td>
                        <td style={styles.td}>
                          <span style={getReviewStatusStyle(review_status)}>
                            {statusContent.icon}
                            {statusContent.text}
                          </span>
                        </td>
                      </tr>
                    );
                  }
                )
              ) : (
                <tr>
                  <td colSpan="9" style={{...styles.td, textAlign: 'center', padding: '48px', color: '#6b7280'}}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "deleteConfirm") deleteSelf();
            else if (alert === "completeDelete") getList();
            else if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          
          .table-wrapper::-webkit-scrollbar {
            height: 8px;
          }
          
          .table-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        }
      `}</style>
    </div>
  );
};

export default SelfApplication;